import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Chip,
  Paper,
  Divider,
  CircularProgress,
} from '@mui/material';
import { Warning, CheckCircle } from '@mui/icons-material';
import { where, orderBy, collection, query, getDocs, onSnapshot, startAt, endAt } from 'firebase/firestore';
import { format } from 'date-fns';
import { ko } from 'date-fns/locale';
import { MainLayout } from '../components/layout/MainLayout';
import { useAuth } from '../contexts/AuthContext';
import { useFirestoreQuery } from '../hooks/useFirestoreQuery';
import { Inquiry } from '../types/inquiry';
import { Route } from '../types/route';
import { Operation } from '../types/operation';
import { AnomalyDetection } from '../types/anomaly';
import { db } from '../lib/firebase';

export const DashboardPage: React.FC = () => {
  const { adminType } = useAuth();
  const navigate = useNavigate();

  // ===== 이상 운행 감지 기능 토글 스위치 =====
  const ENABLE_EARLY_TERMINATION_DETECTION = false; // true: 활성화, false: 비활성화

  // 이상 운행 관련 state
  const [anomalies, setAnomalies] = useState<AnomalyDetection[]>([]);
  const [anomalyLoading, setAnomalyLoading] = useState(false);

  // 노선별로 그룹화된 이상 운행
  interface GroupedAnomaly {
    id: string;
    routeId: string;
    routeName: string;
    operationDate: string;
    operationId?: string;
    vehicleNumber?: string;
    driverName?: string;
    anomalies: AnomalyDetection[];
  }

  const groupedAnomalies: GroupedAnomaly[] = React.useMemo(() => {
    const grouped = new Map<string, GroupedAnomaly>();

    anomalies.forEach(anomaly => {
      // 운행기록이 있으면 operationId를 키로, 없으면 routeId_date를 키로 사용
      const key = anomaly.operationId || `${anomaly.routeId}_${anomaly.operationDate}`;

      if (!grouped.has(key)) {
        grouped.set(key, {
          id: key,
          routeId: anomaly.routeId,
          routeName: anomaly.routeName,
          operationDate: anomaly.operationDate,
          operationId: anomaly.operationId,
          vehicleNumber: anomaly.vehicleNumber,
          driverName: anomaly.driverName,
          anomalies: [],
        });
      }

      grouped.get(key)!.anomalies.push(anomaly);
    });

    return Array.from(grouped.values());
  }, [anomalies]);

  // 권한별 노선 필터
  const getRouteFilter = () => {
    switch (adminType) {
      case 'route1':
        return '강서구청';
      case 'route2':
        return '하단';
      default:
        return null;
    }
  };

  const routeFilter = getRouteFilter();

  // 미처리 민원 조회 (단순문의 제외, 권한별 필터링)
  const constraints = [
    where('isAnswered', '==', false),
    where('category', 'in', ['노선', '서비스']),
    ...(routeFilter ? [where('route', '==', routeFilter)] : []),
    orderBy('createdAt', 'desc'),
  ];

  const { data: inquiries, loading } = useFirestoreQuery<Inquiry>('inquiries', ...constraints);

  const handleInquiryClick = (inquiryId: string) => {
    navigate(`/cs/${inquiryId}`);
  };

  // 이상 운행 감지 로직
  useEffect(() => {
    const detectAnomalies = async () => {
      setAnomalyLoading(true);
      const detectedAnomalies: AnomalyDetection[] = [];
      const today = format(new Date(), 'yyyy-MM-dd');

      try {
        // 1. 모든 노선 조회
        const routesQuery = query(collection(db, 'routes'));
        const routesSnapshot = await getDocs(routesQuery);
        const routes = routesSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        } as Route));

        // 권한별 노선 필터링
        const filteredRoutes = routeFilter
          ? routes.filter(route => route.name.includes(routeFilter))
          : routes;

        // 2. 오늘 날짜의 운행 기록 조회
        const operationsQuery = query(
          collection(db, 'operations'),
          orderBy('__name__'),
          startAt(today),
          endAt(today + '\uf8ff')
        );

        const operationsSnapshot = await getDocs(operationsQuery);
        const operations = operationsSnapshot.docs.map(doc => {
          const docData = doc.data();
          const operationDate = doc.id.split('_')[0];
          return {
            id: doc.id,
            operationDate,
            routeId: docData.routeId || '',
            routeName: docData.routeName || '',
            vehicleNumber: docData.vehicleNumber || '',
            driverName: docData.driverName || '',
            status: docData.status || '',
            startTime: docData.startTime,
            endTime: docData.endTime,
            currentStopIndex: docData.currentStopIndex || 0,
            totalStops: docData.totalStops || 0,
            isDelayed: docData.isDelayed || false,
            delayMinutes: docData.delayMinutes || 0,
            totalPassengers: docData.totalPassengers || 0,
            stationLogs: docData.stationLogs || [],
            isHot: docData.isHot || 0,
            isCold: docData.isCold || 0,
            isDanger: docData.isDanger || 0,
            isTestMode: docData.isTestMode || false,
          } as Operation;
        })
        .filter(op => !op.isTestMode) // 테스트 데이터 제외
        .filter(op => {
          // 권한별 노선 필터링
          if (!routeFilter) return true; // super admin은 모든 데이터

          // routeName 또는 routeId로 필터링
          const route = filteredRoutes.find(r => r.id === op.routeId);
          if (route) {
            return route.name.includes(routeFilter);
          }

          // route를 찾지 못했으면 routeName으로 필터링
          return op.routeName.includes(routeFilter);
        });

        // 3. 운행기록 미작성 감지
        const now = new Date();
        const currentDay = now.getDay(); // 0=일, 1=월, ..., 6=토
        const dayOfWeek = currentDay === 0 ? 7 : currentDay; // 1=월, ..., 7=일로 변환

        filteredRoutes.forEach(route => {
          // operatingDays가 있으면 오늘이 운행일인지 확인
          if (route.operatingDays && !route.operatingDays.includes(dayOfWeek)) {
            return; // 오늘은 운행일이 아님
          }

          // 노선의 출발시간 파싱 (HH:mm 형식)
          if (route.departureTime) {
            const [hours, minutes] = route.departureTime.split(':').map(Number);
            const departureDateTime = new Date();
            departureDateTime.setHours(hours, minutes, 0, 0);

            // 현재 시각이 출발시간 이전이면 아직 감지하지 않음
            if (now < departureDateTime) {
              return;
            }
          }

          // 오늘 날짜의 해당 노선 운행 기록이 있는지 확인
          const hasOperation = operations.some(op => op.routeId === route.id);

          if (!hasOperation) {
            detectedAnomalies.push({
              id: `missing_${route.id}_${today}`,
              type: 'MISSING_OPERATION',
              routeId: route.id,
              routeName: route.name,
              operationDate: today,
              description: `${route.name} 노선의 운행 기록이 작성되지 않았습니다. (출발시간: ${route.departureTime})`,
              detectedAt: new Date(),
              severity: 'high',
            });
          }
        });

        // 4. 조기 출발 감지 (퇴근 노선만, stationLogs의 delayMinutes가 연속 3개 이상 음수)
        operations.forEach(operation => {
          // 해당 운행의 노선 정보 조회
          const route = filteredRoutes.find(r => r.id === operation.routeId);

          // 퇴근 노선(isCommuting = false)인 경우에만 조기 출발 체크
          if (route && route.isCommuting === false) {
            if (operation.stationLogs && operation.stationLogs.length >= 3) {
              let consecutiveNegative = 0;
              let maxConsecutiveNegative = 0;

              for (const log of operation.stationLogs) {
                if (log.delayMinutes !== undefined && log.delayMinutes < 0) {
                  consecutiveNegative++;
                  maxConsecutiveNegative = Math.max(maxConsecutiveNegative, consecutiveNegative);
                } else {
                  consecutiveNegative = 0;
                }
              }

              if (maxConsecutiveNegative >= 3) {
                detectedAnomalies.push({
                  id: `early_${operation.id}`,
                  type: 'EARLY_DEPARTURE',
                  routeId: operation.routeId,
                  routeName: operation.routeName || operation.routeId,
                  operationDate: operation.operationDate,
                  operationId: operation.id,
                  vehicleNumber: operation.vehicleNumber,
                  driverName: operation.driverName,
                  description: `${maxConsecutiveNegative}개 정류장에서 연속으로 조기 도착했습니다. (퇴근 노선)`,
                  detectedAt: new Date(),
                  severity: 'medium',
                });
              }
            }
          }
        });

        // 5. 지연 운행 감지 (최대 delayMinutes >= 3)
        operations.forEach(operation => {
          // 권한별 노선 확인 - filteredRoutes에 포함된 노선만 체크
          const route = filteredRoutes.find(r => r.id === operation.routeId);
          if (!route) return; // 권한이 없는 노선은 건너뜀

          if (operation.stationLogs && operation.stationLogs.length > 0) {
            const maxDelay = Math.max(
              ...operation.stationLogs.map(log => log.delayMinutes || 0)
            );

            if (maxDelay >= 3) {
              detectedAnomalies.push({
                id: `delayed_${operation.id}`,
                type: 'DELAYED',
                routeId: operation.routeId,
                routeName: operation.routeName || operation.routeId,
                operationDate: operation.operationDate,
                operationId: operation.id,
                vehicleNumber: operation.vehicleNumber,
                driverName: operation.driverName,
                description: `최대 ${maxDelay}분 지연되었습니다.`,
                detectedAt: new Date(),
                severity: maxDelay >= 10 ? 'high' : 'medium',
              });
            }
          }
        });

        // 6. 조기 운행 종료 감지 (토글 가능)
        if (ENABLE_EARLY_TERMINATION_DETECTION) {
          operations.forEach(operation => {
            // 권한별 노선 확인 - filteredRoutes에 포함된 노선만 체크
            const route = filteredRoutes.find(r => r.id === operation.routeId);
            if (!route) return; // 권한이 없는 노선은 건너뜀

            const totalStops = route.stops.length || operation.totalStops || 0;
            const currentStopIndex = operation.currentStopIndex || 0;

            // currentStopIndex가 총 정류장 수보다 2개 이상 적고
            // status가 COMPLETED이거나 5분 이상 업데이트가 없는 경우
            if (totalStops > 0 && currentStopIndex < totalStops - 2) {
              // endTime 또는 startTime을 기준으로 마지막 업데이트 시간 계산
              const lastUpdateTime = operation.endTime?.toDate() || operation.startTime?.toDate();

              if (lastUpdateTime) {
                const minutesSinceUpdate = (new Date().getTime() - lastUpdateTime.getTime()) / (1000 * 60);

                if (operation.status === 'COMPLETED' || minutesSinceUpdate >= 5) {
                  detectedAnomalies.push({
                    id: `early_term_${operation.id}`,
                    type: 'EARLY_TERMINATION',
                    routeId: operation.routeId,
                    routeName: operation.routeName || operation.routeId,
                    operationDate: operation.operationDate,
                    operationId: operation.id,
                    vehicleNumber: operation.vehicleNumber,
                    driverName: operation.driverName,
                    description: `${currentStopIndex}/${totalStops} 정류장에서 운행이 조기 종료되었습니다.`,
                    detectedAt: new Date(),
                    severity: 'high',
                  });
                }
              }
            }
          });
        }

        setAnomalies(detectedAnomalies);
      } catch (error) {
        console.error('Error detecting anomalies:', error);
      } finally {
        setAnomalyLoading(false);
      }
    };

    detectAnomalies();

    // 1분마다 이상 운행 재감지
    const interval = setInterval(detectAnomalies, 60000);

    return () => clearInterval(interval);
  }, [routeFilter]);

  const getCategoryColor = (category: string) => {
    switch (category) {
      case '노선':
        return 'primary';
      case '서비스':
        return 'secondary';
      default:
        return 'default';
    }
  };

  const getAnomalyColor = (type: string) => {
    switch (type) {
      case 'MISSING_OPERATION':
        return 'error';
      case 'EARLY_DEPARTURE':
        return 'warning';
      case 'DELAYED':
        return 'warning';
      case 'EARLY_TERMINATION':
        return 'error';
      default:
        return 'default';
    }
  };

  const getAnomalyLabel = (type: string) => {
    switch (type) {
      case 'MISSING_OPERATION':
        return '운행기록 미작성';
      case 'EARLY_DEPARTURE':
        return '조기 출발';
      case 'DELAYED':
        return '지연 운행';
      case 'EARLY_TERMINATION':
        return '조기 운행 종료';
      default:
        return type;
    }
  };

  return (
    <MainLayout>
      <Box>
        <Typography variant="h4" component="h1" fontWeight={700} gutterBottom>
          대시보드
        </Typography>
        <Typography variant="body2" color="text.secondary" gutterBottom>
          부산항 신항 셔틀버스 관리자 대시보드입니다.
        </Typography>

        <Box sx={{ display: 'flex', gap: 3, mt: 2, flexWrap: 'wrap' }}>
          {/* 통계 카드 */}
          <Box sx={{ flex: '1 1 200px', minWidth: 200 }}>
            <Card sx={{ height: '100%' }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box>
                    <Typography color="text.secondary" gutterBottom variant="body2">
                      미처리 민원
                    </Typography>
                    <Typography variant="h4" component="div" fontWeight={700}>
                      {loading ? '-' : inquiries.length}
                    </Typography>
                  </Box>
                  <Warning sx={{ fontSize: 48, color: '#f59e0b' }} />
                </Box>
              </CardContent>
            </Card>
          </Box>

          <Box sx={{ flex: '1 1 200px', minWidth: 200 }}>
            <Card sx={{ height: '100%' }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box>
                    <Typography color="text.secondary" gutterBottom variant="body2">
                      처리 완료
                    </Typography>
                    <Typography variant="h4" component="div" fontWeight={700}>
                      -
                    </Typography>
                  </Box>
                  <CheckCircle sx={{ fontSize: 48, color: '#10b981' }} />
                </Box>
              </CardContent>
            </Card>
          </Box>
        </Box>

        {/* 미처리 민원 목록 */}
        <Box sx={{ mt: 3 }}>
            <Paper sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                <Typography variant="h6" fontWeight={600}>
                  미처리 민원 목록
                </Typography>
                <Chip
                  label={`${inquiries.length}건`}
                  color="warning"
                  size="small"
                />
              </Box>
              <Divider sx={{ mb: 2 }} />

              {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                  <CircularProgress />
                </Box>
              ) : inquiries.length === 0 ? (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                  <Typography color="text.secondary">
                    미처리 민원이 없습니다.
                  </Typography>
                </Box>
              ) : (
                <List>
                  {inquiries.map((inquiry, index) => (
                    <React.Fragment key={inquiry.id}>
                      {index > 0 && <Divider />}
                      <ListItem disablePadding>
                        <ListItemButton
                          onClick={() => handleInquiryClick(inquiry.id)}
                          sx={{
                            '&:hover': {
                              backgroundColor: '#f8fafc',
                            },
                          }}
                        >
                        <ListItemText
                          primaryTypographyProps={{ component: 'div' }}
                          secondaryTypographyProps={{ component: 'div' }}
                          primary={
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                              <Typography variant="body1" fontWeight={600}>
                                {inquiry.title}
                              </Typography>
                              <Chip
                                label={inquiry.category}
                                color={getCategoryColor(inquiry.category)}
                                size="small"
                              />
                              <Chip
                                label={inquiry.route}
                                variant="outlined"
                                size="small"
                              />
                            </Box>
                          }
                          secondary={
                            <Box>
                              <Typography variant="body2" color="text.secondary" noWrap>
                                {inquiry.content}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {inquiry.organization || '미지정'} • {' '}
                                {inquiry.createdAt
                                  ? format(inquiry.createdAt.toDate(), 'yyyy년 MM월 dd일 HH:mm', { locale: ko })
                                  : '-'}
                              </Typography>
                            </Box>
                          }
                        />
                        </ListItemButton>
                      </ListItem>
                    </React.Fragment>
                  ))}
                </List>
              )}
            </Paper>
        </Box>

        {/* 이상 운행 추정 목록 */}
        <Box sx={{ mt: 3 }}>
          <Paper sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
              <Typography variant="h6" fontWeight={600}>
                이상 운행 추정 목록
              </Typography>
              <Chip
                label={`${groupedAnomalies.length}건`}
                color={groupedAnomalies.length > 0 ? 'error' : 'default'}
                size="small"
              />
            </Box>
            <Divider sx={{ mb: 2 }} />

            {anomalyLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress />
              </Box>
            ) : groupedAnomalies.length === 0 ? (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <Typography color="text.secondary">
                  감지된 이상 운행이 없습니다.
                </Typography>
              </Box>
            ) : (
              <List>
                {groupedAnomalies.map((group, index) => (
                  <React.Fragment key={group.id}>
                    {index > 0 && <Divider />}
                    <ListItem disablePadding>
                      <ListItemButton
                        onClick={() => {
                          if (group.operationId) {
                            navigate(`/operation/${group.operationId}`);
                          }
                        }}
                        sx={{
                          '&:hover': {
                            backgroundColor: '#f8fafc',
                          },
                        }}
                        disabled={!group.operationId}
                      >
                        <ListItemText
                          primaryTypographyProps={{ component: 'div' }}
                          secondaryTypographyProps={{ component: 'div' }}
                          primary={
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5, flexWrap: 'wrap' }}>
                              <Typography variant="body1" fontWeight={600}>
                                {group.routeName}
                              </Typography>
                              {group.vehicleNumber && (
                                <Chip
                                  label={group.vehicleNumber}
                                  variant="outlined"
                                  size="small"
                                />
                              )}
                              {group.anomalies.map((anomaly, idx) => (
                                <Chip
                                  key={idx}
                                  label={getAnomalyLabel(anomaly.type)}
                                  color={getAnomalyColor(anomaly.type)}
                                  size="small"
                                />
                              ))}
                            </Box>
                          }
                          secondary={
                            <Box>
                              {group.anomalies.map((anomaly, idx) => (
                                <Typography
                                  key={idx}
                                  variant="body2"
                                  color="text.secondary"
                                  sx={{ mb: idx < group.anomalies.length - 1 ? 0.5 : 0 }}
                                >
                                  • {anomaly.description}
                                </Typography>
                              ))}
                              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                                {group.driverName || '미지정'} • {format(new Date(group.operationDate), 'yyyy년 MM월 dd일', { locale: ko })}
                              </Typography>
                            </Box>
                          }
                        />
                      </ListItemButton>
                    </ListItem>
                  </React.Fragment>
                ))}
              </List>
            )}
          </Paper>
        </Box>
      </Box>
    </MainLayout>
  );
};
